const fractionsRegex = /(^\d+)\/(\d+$)/
const decimalCommaRegex = /^\d+[, ]*\d+$/

// rounds a number to the desired number of decimal places
// returns the result as a number
function roundTo (num: number, decimalPlaces: number): number {
  const factor = Math.pow(10, decimalPlaces)
  return Math.round(Number(num) * factor) / factor
}

// deconstruct a number to integer and fraction part
// returns it as an array as [integer, fraction]
function deconstruct (num: number): [number, number] {
  if (num === 0) {
    return [0, 0]
  }

  // +1 or -1
  const sign = Math.abs(num) / num
  const integerPart = Math.floor(Math.abs(num)) * sign
  const decimalFractionPart = (Math.abs(num) - Math.abs(integerPart)) * sign

  return [integerPart, decimalFractionPart]
}

const vulgarFractionMap = [
  { lo: 0.23, hi: 0.27, symbol: '¼' },
  { lo: 0.32, hi: 0.36, symbol: '⅓' },
  { lo: 0.64, hi: 0.68, symbol: '⅔' },
  { lo: 0.73, hi: 0.77, symbol: '¾' }
]

export function useIngredientQuantity () {
  function isNumber (str: unknown): boolean {
    return !isNaN(Number(str))
  }

  function isVulgarFraction (str: string): boolean {
    return fractionsRegex.test(str)
  }

  function fromVulgarFraction (str: string): number | string {
    if (isVulgarFraction(str)) {
      const match = str.match(fractionsRegex)!
      return Number(match[1]) / Number(match[2])
    }

    // if it's not a fraction, we just return whatever we got
    return str
  }

  // formats the provided number to a nice string
  function formatQuantity (num: number): string {
    if (num === 0) {
      return ''
    }

    let signString = ''
    const [integer, fraction] = deconstruct(num)
    const twoDecimals = roundTo(fraction, 2)
    const oneDecimal = roundTo(fraction, 1)
    let separatorString = ''

    let integerString = integer !== 0 ? integer.toString() : ''
    let fractionString: string | undefined

    if (oneDecimal === 0) {
      fractionString = ''
    } else {
      fractionString = vulgarFractionMap.find(f => f.lo <= twoDecimals && f.hi >= twoDecimals)?.symbol
      if (fractionString === undefined && oneDecimal === 0.5) {
        fractionString = '½'
      }
      if (fractionString === undefined) {
        fractionString = Number(oneDecimal * 10).toString()
        separatorString = ','
        if (integer === 0) {
          integerString = '0'
        }
      } else {
        if (integerString !== '') {
          separatorString = ' '
        } else if (oneDecimal < 0) {
          signString = '-'
        }
      }
    }

    return `${signString}${integerString}${separatorString}${fractionString}`
  }

  // Tries to create a number out of whatever is in the ingredient field.
  // If it cannot parse the quantity, it returns NaN
  function parseQuantity (quantity: unknown): number {
    const str = String(quantity).trim()

    // "normal" numbers, integers or fractions with decimal point, such as 1.2
    if (isNumber(str)) {
      return Number(str)
    }

    // numbers with decimal comma (like 1,2, 0,2)
    if (decimalCommaRegex.test(str)) {
      return Number(
        str
          .replace(',', '.') // replace decimal comma to decimal point
          .replace(' ', '') // remove whitespaces inside the number if there's any
      )
    }

    // numbers in vulgar fraction format such as 1/2, 1/3 or 3 1/2
    const parts = str.split(' ')
    if (parts.every(p => isNumber(p) || isVulgarFraction(p))) {
      return parts.reduce((sum, p) => {
        return sum + (isNumber(p) ? Number(p) : (fromVulgarFraction(p) as number))
      }, 0)
    }

    // unparseable stuff remains NaN
    return Number.NaN
  }

  return {
    formatQuantity,
    parseQuantity,
    isNumber,
    isVulgarFraction,
    fromVulgarFraction
  }
}
